import autoSearchDirectory from './autoSearchDirectory';
import backfillCache from './backfillCache';
import datapoints from '../../datapoints';
import FindControlViewState from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import getPersonasFromReadWriteRecipientList from 'owa-recipient-common/lib/utils/getPersonasFromReadWriteRecipientList';
import postProcessSuggestionsResult from './postProcessSuggestionsResult';
import processCacheResult from 'owa-readwrite-recipient-well/lib/actions/people/processCacheResult';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import setIsSearching from '../setIsSearching';
import store from '../../store/store';
import { action } from 'satcheljs/lib/legacy';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { FindResultType } from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import { getPeopleSuggestionsFromCache } from 'owa-recipient-cache/lib/actions/getPeopleSuggestionsFromCache';
import updateCurrentRenderedQueryString from 'owa-readwrite-recipient-well-fabric/lib/actions/updateCurrentRenderedQueryString';
import { populateFeedbackStateCache } from 'owa-readwrite-recipient-well-fabric/lib/utils/populateFeedbackState';
import { now } from 'owa-datetime';

const MAX_ENTRIES_TYPEDOWN = 5;
const FIND_PEOPLE_REQUEST_COUNT_THRESHOLD = 3;

let getCacheResults = (
    queryString: string,
    recipients: ReadWriteRecipientViewState[],
    recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
    appendResults: boolean,
    resolveIfSingle: boolean
): void => {
    const transactionStartTime = now();
    let cacheResults = getPeopleSuggestionsFromCache(
        queryString,
        getPersonasFromReadWriteRecipientList(recipients)
    );

    populateFeedbackStateCache(recipientWell, transactionStartTime, cacheResults);
    processCacheResult(recipientWell, cacheResults, recipients, appendResults, resolveIfSingle);
};

export default action('findPeopleCacheFirst')(async function findPeopleCacheFirst(
    recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
    queryString: string,
    recipients: ReadWriteRecipientViewState[],
    appendResults: boolean,
    resolveIfSingle: boolean
): Promise<void> {
    // Initialization of variables
    let resultType: FindResultType = FindResultType.Cache;
    let datapoint: PerformanceDatapoint = new PerformanceDatapoint(
        datapoints.SearchPeopleIndex.name
    );
    recipientWell.hasSearchedDirectory = false;

    setIsSearching(recipientWell, true);

    // Try to find people using the cache
    getCacheResults(queryString, recipients, recipientWell, appendResults, resolveIfSingle);
    updateCurrentRenderedQueryString(recipientWell, queryString);

    if (recipientWell.findResultSet.length < MAX_ENTRIES_TYPEDOWN) {
        try {
            if (
                recipientWell.findResultSet.length > 0 &&
                store.pendingFindPeopleRequests < FIND_PEOPLE_REQUEST_COUNT_THRESHOLD
            ) {
                // If there are cache results and we are under the people request threshold, append the results if the cache produced less than 5 results, and backfill the cache with the server results
                // We do not want to issue a live call if we are over the threshold limit, since we know the UI will display at least one result, we don't want to bog the service down if it is not necessary
                backfillCache(recipientWell, queryString);
                resultType = FindResultType.MixedCacheLive;
            } else if (recipientWell.findResultSet.length == 0) {
                // If there are no cache results, issue a live call regardless of the threshold limit in order to try and eventually show some results.
                // Backfill the cache with server results and update the UI with the server results
                await backfillCache(recipientWell, queryString);
                resultType = FindResultType.FindPeople;
            }
        } catch (error) {
            store.pendingFindPeopleRequests--;
            datapoint.endWithError(DatapointStatus.ServerError, error);
        }
    }

    if (recipientWell.findResultSet.length > 0) {
        await postProcessSuggestionsResult(recipientWell, recipients, resultType, resolveIfSingle);
    } else {
        // If there are still no results, then query AD
        await autoSearchDirectory(recipientWell, recipients, queryString, resolveIfSingle);
        resultType = FindResultType.SearchDirectory;
    }

    setIsSearching(recipientWell, false);

    // Instrument the result type in the datapoint
    datapoint.addCustomData([resultType]);
    datapoint.end();
});
